/**
 * MessageRecoveryPanel.jsx
 * 
 * Componente para escanear y recuperar mensajes no leídos de WhatsApp
 * cuando el Desktop Client estuvo desconectado.
 * 
 * Funcionalidades:
 * - Botón para iniciar escaneo manual
 * - Indicador de progreso en tiempo real
 * - Notificación de escaneo automático (si está habilitado)
 */

import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { RefreshCw, Inbox, CheckCircle, AlertCircle, X, Loader2, MessageSquare, Clock, Coffee, Shield, Zap, Minus, Maximize2 } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useRecovery } from '../../context/RecoveryContext';
import { API_URL } from '../../config/api';

// Estados posibles del sistema de recuperación
const RECOVERY_STATUS = {
  IDLE: 'idle',
  LOADING_CONFIG: 'loading_config',
  SCANNING: 'scanning',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  ERROR: 'error',
  AUTO_SCANNING: 'auto_scanning'
};

const MessageRecoveryPanel = () => {
  const { currentUser } = useContext(AuthContext);
  const { socket } = useSocket();
  const {
    isPanelOpen,
    setIsPanelOpen,
    isMinimized,
    minimizePanel,
    maximizePanel,
    closePanel,
    scanTrigger,
    setIsScanning
  } = useRecovery();

  // Estado del sistema de recuperación
  const [status, setStatus] = useState(RECOVERY_STATUS.IDLE);
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    messagesProcessed: 0,
    totalProcessed: 0,
    sessionLimit: 100,
    batchNumber: 0,
    chatId: null
  });
  const [pauseInfo, setPauseInfo] = useState(null); // Info de pausa anti-spam
  const [warning, setWarning] = useState(null); // Advertencia de lote grande
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [pauseCountdown, setPauseCountdown] = useState(0); // Countdown de pausa

  // CORRECCIÓN: Ref para rastrear si el componente está montado
  const isMountedRef = useRef(true);

  // CORRECCIÓN: Ref para rastrear el interval del countdown
  const countdownIntervalRef = useRef(null);

  // CORRECCIÓN: Ref para rastrear el último trigger de escaneo ejecutado
  const scanTriggerRef = useRef(0);

  // CORRECCIÓN: useEffect para limpiar al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      // CORRECCIÓN: Limpiar interval si existe
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []);

  // Verificar si el sistema de recuperación está disponible
  const checkRecoveryConfig = useCallback(async () => {
    if (!currentUser) return;

    try {
      if (isMountedRef.current) {
        setStatus(RECOVERY_STATUS.LOADING_CONFIG);
      }

      const token = await currentUser.getIdToken();
      if (!isMountedRef.current) return;

      const response = await fetch(`${API_URL}/recovery/config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!isMountedRef.current) return;

      if (response.ok) {
        if (isMountedRef.current) setIsEnabled(true);
      } else {
        if (isMountedRef.current) setIsEnabled(false);
      }
    } catch (err) {
      console.warn('[Recovery] Config check failed:', err);
      if (isMountedRef.current) setIsEnabled(true);
    } finally {
      if (isMountedRef.current) {
        setStatus(RECOVERY_STATUS.IDLE);
      }
    }
  }, [currentUser]);

  // Verificar configuración al montar
  useEffect(() => {
    checkRecoveryConfig();
  }, [checkRecoveryConfig]);

  // Función START SCAN (Extraída para reutilizar)
  const handleStartScan = useCallback(async () => {
    if (!currentUser || status === RECOVERY_STATUS.SCANNING) return;

    try {
      if (isMountedRef.current) {
        setStatus(RECOVERY_STATUS.SCANNING);
        setProgress({ current: 0, total: 0, messagesProcessed: 0, chatId: null });
        setResult(null);
        setError(null);
        // Asegurar que el panel se abra (esto ya lo hace el context, pero doble check)
        setIsPanelOpen(true);
      }

      const token = await currentUser.getIdToken();
      if (!isMountedRef.current) return;

      const response = await fetch(`${API_URL}/recovery/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!isMountedRef.current) return;

      const data = await response.json();
      if (!isMountedRef.current) return;

      if (!data.success) {
        if (isMountedRef.current) {
          setStatus(RECOVERY_STATUS.ERROR);
          setError(data.error || 'Error iniciando el escaneo');
        }
      }
    } catch (err) {
      console.error('[Recovery] Start scan error:', err);
      if (isMountedRef.current) {
        setStatus(RECOVERY_STATUS.ERROR);
        setError(err.message || 'Error de conexión');
      }
    }
  }, [currentUser, status, setIsPanelOpen]);

  // Escuchar trigger del context
  useEffect(() => {
    if (scanTrigger > 0 && scanTrigger !== scanTriggerRef.current) {
      scanTriggerRef.current = scanTrigger;
      handleStartScan();
    }
  }, [scanTrigger, handleStartScan]); /* [COLMENA-FIX] SEM-003: TODO — Este useEffect + useState debería ser useMemo.
  // Ejemplo: const derivedValue = useMemo(() => transform(dep), [dep]);
  */ /* [COLMENA-FIX] SEM-003: TODO — Este useEffect + useState debería ser useMemo.
   // Ejemplo: const derivedValue = useMemo(() => transform(dep), [dep]);
   */useEffect(() => {const scanning = status === RECOVERY_STATUS.SCANNING || status === RECOVERY_STATUS.AUTO_SCANNING || status === RECOVERY_STATUS.PAUSED;setIsScanning(scanning);
    }, [status, setIsScanning]);

  // Escuchar eventos WebSocket de recuperación
  useEffect(() => {
    if (!socket) return;

    // Escaneo iniciado (manual o automático)
    const handleStarted = (data) => {
      console.log('[Recovery] Scan started:', data);
      if (!isMountedRef.current) return;

      setStatus(RECOVERY_STATUS.SCANNING);
      setProgress({ current: 0, total: 0, messagesProcessed: 0, totalProcessed: 0, sessionLimit: 100, batchNumber: 0, chatId: null });
      setPauseInfo(null);
      setWarning(null);
      setResult(null);
      setError(null);

      // ABRIR PANEL AUTOMÁTICAMENTE SI EMPIEZA UN ESCANEO
      setIsPanelOpen(true);
    };

    // Escaneo automático iniciado
    const handleAutoScanStarted = (data) => {
      console.log('[Recovery] Auto scan started:', data);
      if (!isMountedRef.current) return;

      setStatus(RECOVERY_STATUS.AUTO_SCANNING);
      setProgress({ current: 0, total: 0, messagesProcessed: 0, totalProcessed: 0, sessionLimit: 100, batchNumber: 0, chatId: null });
      setPauseInfo(null);
      setWarning(null);
      setResult(null);
      setError(null);

      setIsPanelOpen(true);
    };

    // Progreso del escaneo
    const handleProgress = (data) => {
      // console.log('[Recovery] Progress:', data);
      if (!isMountedRef.current) return;

      setStatus(RECOVERY_STATUS.SCANNING);
      setPauseInfo(null);
      setProgress({
        current: data.current || 0,
        total: data.total || 0,
        messagesProcessed: data.messagesProcessed || 0,
        totalProcessed: data.totalProcessed || 0,
        sessionLimit: data.sessionLimit || 100,
        batchNumber: data.batchNumber || 0,
        chatId: data.chatId
      });
    };

    // Advertencia de lote grande
    const handleWarning = (data) => {
      if (!isMountedRef.current) return;
      setWarning(data);
    };

    // Pausa anti-spam
    const handlePause = (data) => {
      if (!isMountedRef.current) return;

      setStatus(RECOVERY_STATUS.PAUSED);
      setPauseInfo(data);

      const duration = data.duration || 0;
      setPauseCountdown(Math.ceil(duration / 1000));

      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      countdownIntervalRef.current = setInterval(() => {
        if (!isMountedRef.current) {
          if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
          return;
        }

        setPauseCountdown((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    // Escaneo completado
    const handleCompleted = (summary) => {
      if (!isMountedRef.current) return;

      setStatus(RECOVERY_STATUS.COMPLETED);
      setResult(summary);
      setPauseInfo(null);
      setPauseCountdown(0);

      // Auto-ocultar después de 10 segundos si no hubo mensajes
      if (summary.messagesProcessed === 0) {
        setTimeout(() => {
          if (isMountedRef.current) {
            closePanel(); // Usar función del contexto
            setStatus(RECOVERY_STATUS.IDLE);
          }
        }, 5000);
      }
    };

    // Escaneo automático completado
    const handleAutoScanCompleted = (summary) => {
      if (!isMountedRef.current) return;

      setStatus(RECOVERY_STATUS.COMPLETED);
      setResult(summary);
      setPauseInfo(null);
      setPauseCountdown(0);

      setTimeout(() => {
        if (isMountedRef.current) {
          closePanel();
          setStatus(RECOVERY_STATUS.IDLE);
        }
      }, 5000);
    };

    // Error en el escaneo
    const handleError = (data) => {
      if (!isMountedRef.current) return;

      setStatus(RECOVERY_STATUS.ERROR);
      setError(data.message || 'Error desconocido durante la recuperación');
      setPauseInfo(null);
      setPauseCountdown(0);
    };

    const handleAutoScanError = (data) => {
      if (!isMountedRef.current) return;
      setStatus(RECOVERY_STATUS.ERROR);
      setError(data.message || 'Error desconocido durante la recuperación');
      setPauseInfo(null);
      setPauseCountdown(0);
    };

    // Suscribirse a eventos
    socket.on('recovery:started', handleStarted);
    socket.on('recovery:auto_scan_started', handleAutoScanStarted);
    socket.on('recovery:progress', handleProgress);
    socket.on('recovery:warning', handleWarning);
    socket.on('recovery:pause', handlePause);
    socket.on('recovery:completed', handleCompleted);
    socket.on('recovery:auto_scan_completed', handleAutoScanCompleted);
    socket.on('recovery:error', handleError);
    socket.on('recovery:auto_scan_error', handleAutoScanError);

    return () => {
      socket.off('recovery:started', handleStarted);
      socket.off('recovery:auto_scan_started', handleAutoScanStarted);
      socket.off('recovery:progress', handleProgress);
      socket.off('recovery:warning', handleWarning);
      socket.off('recovery:pause', handlePause);
      socket.off('recovery:completed', handleCompleted);
      socket.off('recovery:auto_scan_completed', handleAutoScanCompleted);
      socket.off('recovery:error', handleError);
      socket.off('recovery:auto_scan_error', handleAutoScanError);
    };
  }, [socket, setIsPanelOpen, closePanel]);

  // Cerrar panel manual
  const handleClose = () => {
    closePanel();
    if (status === RECOVERY_STATUS.COMPLETED || status === RECOVERY_STATUS.ERROR) {
      setStatus(RECOVERY_STATUS.IDLE);
    }
  };

  // Si no está habilitado, no mostrar nada
  if (!isEnabled) return null;

  // Si el panel no está abierto, no renderizar NADA (o renderizar un trigger oculto si fuera necesario, pero aquí lo manejamos con Context)
  if (!isPanelOpen) return null;

  // --- MODO MINIMIZADO (Botón Flotante) ---
  if (isMinimized) {
    return (
      <div className="fixed bottom-20 right-4 z-50 animate-in slide-in-from-bottom-2 duration-300">
                <button
          onClick={maximizePanel}
          className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group">
          
                    <div className="relative">
                        {status === RECOVERY_STATUS.SCANNING || status === RECOVERY_STATUS.AUTO_SCANNING ?
            <Loader2 size={18} className="animate-spin text-blue-500" /> :
            status === RECOVERY_STATUS.PAUSED ?
            <Coffee size={18} className="text-amber-500" /> :
            status === RECOVERY_STATUS.COMPLETED ?
            <CheckCircle size={18} className="text-green-500" /> :

            <Inbox size={18} className="text-gray-500" />
            }

                        {/* Indicador de estado */}
                        <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${status === RECOVERY_STATUS.SCANNING ? 'bg-blue-400' :
              status === RECOVERY_STATUS.PAUSED ? 'bg-amber-400' : 'hidden'}`
              }></span>
                            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${status === RECOVERY_STATUS.SCANNING ? 'bg-blue-500' :
              status === RECOVERY_STATUS.PAUSED ? 'bg-amber-500' :
              status === RECOVERY_STATUS.COMPLETED ? 'bg-green-500' : 'bg-gray-400'}`
              }></span>
                        </span>
                    </div>

                    <div className="flex flex-col items-start text-xs">
                        <span className="font-medium text-gray-700 dark:text-gray-200">
                            Recuperación
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 text-[10px]">
                            {status === RECOVERY_STATUS.SCANNING && `${progress.totalProcessed} procesados`}
                            {status === RECOVERY_STATUS.PAUSED && `Pausa ${pauseCountdown}s`}
                            {status === RECOVERY_STATUS.COMPLETED && `Completado`}
                        </span>
                    </div>

                    <Maximize2 size={14} className="text-gray-400 group-hover:text-blue-500 ml-1" />
                </button>
            </div>);

  }

  // --- MODO EXPANDIDO (Panel Flotante Global) ---
  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)] animate-in slide-in-from-bottom-2 duration-200">
            <div className="p-4 rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-2xl ring-1 ring-black/5">
                {/* Header del panel */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                        <div className={`p-1.5 rounded-lg ${status === RECOVERY_STATUS.SCANNING ? 'bg-blue-100 text-blue-600' :
            status === RECOVERY_STATUS.PAUSED ? 'bg-amber-100 text-amber-600' :
            status === RECOVERY_STATUS.COMPLETED ? 'bg-green-100 text-green-600' :
            status === RECOVERY_STATUS.ERROR ? 'bg-red-100 text-red-600' :
            'bg-gray-100 text-gray-600'}`
            }>
                            {status === RECOVERY_STATUS.SCANNING && <Loader2 size={16} className="animate-spin" />}
                            {status === RECOVERY_STATUS.AUTO_SCANNING && <RefreshCw size={16} className="animate-spin" />}
                            {status === RECOVERY_STATUS.PAUSED && <Coffee size={16} />}
                            {status === RECOVERY_STATUS.COMPLETED && <CheckCircle size={16} />}
                            {status === RECOVERY_STATUS.ERROR && <AlertCircle size={16} />}
                            {status === RECOVERY_STATUS.IDLE && <Inbox size={16} />}
                        </div>

                        <div className="flex flex-col">
                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">
                                Sistema de Recuperación
                            </span>
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                {status === RECOVERY_STATUS.SCANNING && 'Escaneando chats...'}
                                {status === RECOVERY_STATUS.AUTO_SCANNING && 'Escaneo automático'}
                                {status === RECOVERY_STATUS.PAUSED && 'Esperando anti-spam'}
                                {status === RECOVERY_STATUS.COMPLETED && 'Proceso finalizado'}
                                {status === RECOVERY_STATUS.ERROR && 'Error detectado'}
                                {status === RECOVERY_STATUS.IDLE && 'Listo para escanear'}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
              onClick={minimizePanel}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Minimizar">
              
                            <Minus size={16} />
                        </button>
                        <button
              onClick={handleClose}
              className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
              title="Cerrar">
              
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Advertencia de lote grande */}
                {warning &&
        <div className="mb-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50">
                        <div className="flex items-start gap-2.5">
                            <Clock size={15} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-amber-700 dark:text-amber-300">
                                <span className="font-semibold block mb-0.5">Lote grande detectado</span>
                                <span className="opacity-90">~{warning.estimatedMessages} mensajes pendientes. Tiempo estimado: {warning.estimatedMinutes} min.</span>
                            </div>
                        </div>
                    </div>
        }

                {/* Contenido según estado - Scanning/Paused */}
                {(status === RECOVERY_STATUS.SCANNING || status === RECOVERY_STATUS.AUTO_SCANNING || status === RECOVERY_STATUS.PAUSED) &&
        <div className="space-y-4">
                        {/* Barra de progreso principal - Mensajes totales */}
                        <div>
                            <div className="flex justify-between text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                                <span className="flex items-center gap-1.5">
                                    <Zap size={12} className="text-blue-500" />
                                    Progreso global
                                </span>
                                <span>{Math.round(progress.totalProcessed / progress.sessionLimit * 100)}%</span>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-gray-900/50 rounded-full h-3 overflow-hidden border border-gray-100 dark:border-gray-700">
                                <div
                className={`h-full rounded-full transition-all duration-500 ease-out ${status === RECOVERY_STATUS.PAUSED ?
                'bg-amber-500' :
                'bg-gradient-to-r from-blue-500 to-indigo-600'}`
                }
                style={{ width: `${Math.min(progress.totalProcessed / progress.sessionLimit * 100, 100)}%` }} />
              
                            </div>
                            <div className="flex justify-between mt-1 text-[10px] text-gray-400">
                                <span>{progress.totalProcessed} mensajes</span>
                                <span>Límite: {progress.sessionLimit}</span>
                            </div>
                        </div>

                        {/* Barra de progreso secundaria - Chats */}
                        {progress.total > 0 &&
          <div className="bg-gray-50 dark:bg-gray-900/30 p-2.5 rounded-lg border border-gray-100 dark:border-gray-700">
                                <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-1.5">
                                    <span>Chats activos</span>
                                    <span className="font-mono">{progress.current}/{progress.total}</span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                    <div
                className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progress.current / progress.total * 100}%` }} />
              
                                </div>
                            </div>
          }

                        {/* Estado de pausa */}
                        {status === RECOVERY_STATUS.PAUSED && pauseInfo &&
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 flex flex-col items-center text-center">
                                <Shield size={20} className="text-amber-500 mb-2" />
                                <span className="text-2xl font-bold text-amber-600 dark:text-amber-400 font-mono mb-1">
                                    {pauseCountdown}s
                                </span>
                                <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                                    {pauseInfo.type === 'super_pause' ? 'Enfriamiento del sistema' : 'Pausa de seguridad'}
                                </span>
                                {pauseInfo.reason &&
            <p className="text-[10px] text-amber-600/80 dark:text-amber-500/80 mt-1 max-w-[200px]">
                                        {pauseInfo.reason}
                                    </p>
            }
                            </div>
          }

                        {/* StatsFooter */}
                        <div className="flex items-center gap-3 pt-2 text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                            {progress.batchNumber > 0 &&
            <span className="flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                                    Lote {progress.batchNumber}
                                </span>
            }
                            <span className="ml-auto">
                                ID: {progress.chatId ? progress.chatId.substring(0, 10) + '...' : 'Iniciando'}
                            </span>
                        </div>
                    </div>
        }

                {status === RECOVERY_STATUS.COMPLETED && result &&
        <div className="space-y-4 py-2">
                        <div className="flex items-center justify-center">
                            <div className="w-16 h-16 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-2">
                                <CheckCircle size={32} className="text-green-500" />
                            </div>
                        </div>

                        <div className="text-center">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">¡Recuperación Exitosa!</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Se han procesado todos los mensajes pendientes.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <div className="bg-gray-50 dark:bg-gray-900/30 p-2 rounded-lg text-center border border-gray-100 dark:border-gray-700">
                                <span className="block text-xl font-bold text-blue-600 dark:text-blue-400">{result.chatsWithUnread || 0}</span>
                                <span className="text-[10px] text-gray-500 uppercase tracking-wide">Chats</span>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-900/30 p-2 rounded-lg text-center border border-gray-100 dark:border-gray-700">
                                <span className="block text-xl font-bold text-green-600 dark:text-green-400">{result.messagesProcessed || 0}</span>
                                <span className="text-[10px] text-gray-500 uppercase tracking-wide">Mensajes</span>
                            </div>
                        </div>

                        {result.messagesSkipped > 0 &&
          <div className="text-[11px] text-gray-400 text-center">
                                + {result.messagesSkipped} mensajes omitidos (duplicados)
                            </div>
          }

                        <div className="pt-2">
                            <button
              onClick={handleClose}
              className="w-full py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs font-semibold rounded-lg transition-colors">
              
                                Cerrar Panel
                            </button>
                        </div>
                    </div>
        }

                {status === RECOVERY_STATUS.ERROR && error &&
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
                        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
        }
            </div>
        </div>);

};

export default MessageRecoveryPanel;